import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect, useMemo, useRef } from "react"

export type MaintenanceInfo = {
    maintenance?: boolean
    message?: string
    untilIso?: string
    updatedAtIso?: string
}

type MaintenanceContextType = {
    maintenanceInfo: MaintenanceInfo | undefined
    maintenanceActive: boolean
    setMaintenanceFromError: (error: Error | string) => void
}

const MaintenanceContext = createContext<MaintenanceContextType | undefined>(undefined)

// Globaler Zugriff für den Apollo Error Link. Im Embed-Modus mit mehreren Modulen je Seite gibt es
// mehrere Provider; der Fehler geht an alle, sonst bekäme nur das zuletzt gemountete Modul den Wartungszustand.
const maintenanceSetters = new Set<(error: Error | string) => void>()

export const getGlobalMaintenanceSetter = (): ((error: Error | string) => void) | null =>
    maintenanceSetters.size ? (error) => maintenanceSetters.forEach((setter) => setter(error)) : null

const MAINTENANCE_POLL_INTERVAL_MS = 15000
const MAINTENANCE_ERROR_TTL_MS = 30000
// PUBLIC_URL-Präfix, damit die Datei auch unter dem Build-Subpfad (homepage "/build") aufgelöst wird.
// Prod: "/build/maintenance.json", Dev (PUBLIC_URL leer): "/maintenance.json".
const MAINTENANCE_URL = process.env.REACT_APP_MAINTENANCE_URL || `${process.env.PUBLIC_URL || ""}/maintenance.json`
const DEFAULT_MAINTENANCE_MESSAGE = "Wartungsarbeiten - wir sind in Kuerze wieder da."

// `url`: nur der Embed-Einstieg übergibt eine absolute Adresse (Bundle läuft auf der Kundenseite); iFrame nutzt den Default
export const MaintenanceProvider: React.FC<{ children: ReactNode; url?: string }> = ({ children, url = MAINTENANCE_URL }) => {
    const [maintenanceInfoState, setMaintenanceInfoState] = useState<MaintenanceInfo | undefined>(undefined)
    const [errorInfo, setErrorInfo] = useState<MaintenanceInfo | undefined>(undefined)
    const [errorActiveUntil, setErrorActiveUntil] = useState<number | undefined>(undefined)
    const lastSuccessfulPollAtRef = useRef<number | undefined>(undefined)
    const pollAbortRef = useRef<AbortController | null>(null)

    const setMaintenanceInfo = useCallback((info: MaintenanceInfo | undefined) => {
        setMaintenanceInfoState(info)
        if (info?.maintenance !== true) {
            setErrorActiveUntil(undefined)
            setErrorInfo(undefined)
        }
    }, [])

    const setMaintenanceFromError = useCallback((error: Error | string) => {
        const errorMessage = typeof error === "string" ? error : error.message
        // Prüfe auf typische Maintenance-Fehlermeldungen
        const isMaintenanceError =
            errorMessage.toLowerCase().includes("service unavailable") ||
            errorMessage.toLowerCase().includes("maintenance") ||
            errorMessage.toLowerCase().includes("wartung") ||
            errorMessage.includes("503")

        if (isMaintenanceError) {
            const now = Date.now()
            setErrorActiveUntil(now + MAINTENANCE_ERROR_TTL_MS)
            setErrorInfo({
                maintenance: true,
                message: DEFAULT_MAINTENANCE_MESSAGE,
            })
        }
    }, [])

    useEffect(() => {
        let isMounted = true

        const fetchMaintenance = async () => {
            pollAbortRef.current?.abort()
            const controller = new AbortController()
            pollAbortRef.current = controller

            try {
                const response = await fetch(url, {
                    // Kein eigener Header: im Embed (fremde Herkunft) erzwänge er einen
                    // CORS-Preflight, den nginx mit 405 ablehnt. no-store umgeht den Cache.
                    cache: "no-store",
                    signal: controller.signal,
                })

                if (!response.ok) {
                    throw new Error(`Maintenance endpoint responded with ${response.status}`)
                }

                const data = (await response.json()) as MaintenanceInfo | null | undefined
                if (!isMounted) return

                if (data?.maintenance === true) {
                    setMaintenanceInfo({
                        maintenance: true,
                        message: data.message || DEFAULT_MAINTENANCE_MESSAGE,
                        untilIso: data.untilIso,
                        updatedAtIso: data.updatedAtIso,
                    })
                } else {
                    setMaintenanceInfo(undefined)
                }
                lastSuccessfulPollAtRef.current = Date.now()
            } catch (err) {
                if (!isMounted) return
                if ((err as Error).name === "AbortError") return
            }
        }

        fetchMaintenance()
        const intervalId = window.setInterval(fetchMaintenance, MAINTENANCE_POLL_INTERVAL_MS)

        return () => {
            isMounted = false
            pollAbortRef.current?.abort()
            window.clearInterval(intervalId)
        }
    }, [setMaintenanceInfo, url])

    useEffect(() => {
        if (!errorActiveUntil) return
        const remainingMs = errorActiveUntil - Date.now()
        if (remainingMs <= 0) {
            setErrorActiveUntil(undefined)
            setErrorInfo(undefined)
            return
        }
        const timeoutId = window.setTimeout(() => {
            setErrorActiveUntil(undefined)
            setErrorInfo(undefined)
        }, remainingMs)

        return () => {
            window.clearTimeout(timeoutId)
        }
    }, [errorActiveUntil])

    const allowErrorFallback = useMemo(() => {
        if (!errorActiveUntil) return false
        if (Date.now() > errorActiveUntil) return false
        const lastPoll = lastSuccessfulPollAtRef.current
        if (!lastPoll) return true
        return Date.now() - lastPoll > MAINTENANCE_ERROR_TTL_MS
    }, [errorActiveUntil])

    const maintenanceInfo = maintenanceInfoState ?? (allowErrorFallback ? errorInfo : undefined)
    const maintenanceActive = maintenanceInfo?.maintenance === true

    // Registriere globalen Setter (für Apollo Error Link) – in einem Effect statt im Render-Body
    useEffect(() => {
        maintenanceSetters.add(setMaintenanceFromError)
        return () => {
            maintenanceSetters.delete(setMaintenanceFromError)
        }
    }, [setMaintenanceFromError])

    // Context-Value memoisieren, damit der 15s-Poll keine Consumer (z.B. CoursesTable) neu rendert
    const contextValue = useMemo<MaintenanceContextType>(
        () => ({
            maintenanceInfo,
            maintenanceActive,
            setMaintenanceFromError,
        }),
        [maintenanceInfo, maintenanceActive, setMaintenanceFromError]
    )

    return <MaintenanceContext.Provider value={contextValue}>{children}</MaintenanceContext.Provider>
}

export const useMaintenance = (): MaintenanceContextType => {
    const context = useContext(MaintenanceContext)
    if (!context) {
        throw new Error("useMaintenance must be used within a MaintenanceProvider")
    }
    return context
}
